import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cotizacionesApi, tiendasApi, uploadApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';

const CotizacionNueva = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { sucursalActual } = useSucursal();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tiendas, setTiendas] = useState([]);

  const [formData, setFormData] = useState({
    cliente_nombre: '',
    cliente_direccion: '',
    cliente_telefono: '',
    cliente_correo: '',
    fecha_emision: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    validez_dias: 5,
    mensaje_cotizacion: 'A la espera de sus comentarios, aprovecho para enviarle un cordial saludo.',
    descuento_porcentaje: 0,
    tienda_id: sucursalActual?.id || 1
  });

  const [items, setItems] = useState([
    { nombre: '', descripcion: '', cantidad: 1, precio_unitario: '', imagen_url: '', disponibilidad: 'disponible' }
  ]);

  useEffect(() => {
    cargarTiendas();
    if (isEditing) {
      cargarCotizacion();
    }
  }, [id]);

  useEffect(() => {
    if (sucursalActual && !isEditing) {
      setFormData(prev => ({ ...prev, tienda_id: sucursalActual.id }));
    }
  }, [sucursalActual, isEditing]);

  const cargarTiendas = async () => {
    try {
      const response = await tiendasApi.getAll();
      setTiendas(response.data || []);
    } catch (error) {
      console.error('Error al cargar tiendas:', error);
    }
  };

  const cargarCotizacion = async () => {
    setLoading(true);
    try {
      const cotizacion = await cotizacionesApi.getById(id);
      setFormData({
        cliente_nombre: cotizacion.cliente_nombre || '',
        cliente_direccion: cotizacion.cliente_direccion || '',
        cliente_telefono: cotizacion.cliente_telefono || '',
        cliente_correo: cotizacion.cliente_correo || '',
        fecha_emision: cotizacion.fecha_emision || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
        validez_dias: cotizacion.validez_dias || 5,
        mensaje_cotizacion: cotizacion.mensaje_cotizacion || '',
        descuento_porcentaje: cotizacion.descuento_porcentaje || 0,
        tienda_id: cotizacion.tienda_id || 1
      });
      if (cotizacion.items && cotizacion.items.length > 0) {
        setItems(cotizacion.items.map(item => ({
          nombre: item.nombre || '',
          descripcion: item.descripcion || '',
          cantidad: item.cantidad || 1,
          precio_unitario: item.precio_unitario || '',
          imagen_url: item.imagen_url || '',
          disponibilidad: item.disponibilidad || 'disponible'
        })));
      }
    } catch (error) {
      console.error('Error al cargar cotización:', error);
      setError('Error al cargar la cotización');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const agregarItem = () => {
    setItems(prev => [...prev, { nombre: '', descripcion: '', cantidad: 1, precio_unitario: '', imagen_url: '', disponibilidad: 'disponible' }]);
  };

  const eliminarItem = (index) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calcularSubtotal = () => {
    return items.reduce((sum, item) => {
      const cantidad = parseInt(item.cantidad) || 0;
      const precio = parseFloat(item.precio_unitario) || 0;
      return sum + (cantidad * precio);
    }, 0);
  };

  const calcularDescuento = () => {
    const subtotal = calcularSubtotal();
    const porcentaje = parseFloat(formData.descuento_porcentaje) || 0;
    return subtotal * (porcentaje / 100);
  };

  const calcularTotal = () => {
    return calcularSubtotal() - calcularDescuento();
  };

  const formatearMoneda = (valor) => {
    return `Q.${parseFloat(valor || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const [uploadingImage, setUploadingImage] = useState({});

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use JPG, PNG, GIF o WebP');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe exceder 5MB');
      return;
    }

    setUploadingImage(prev => ({ ...prev, [index]: true }));
    setError('');

    try {
      const response = await uploadApi.subirImagen(file);
      if (response.success && response.url) {
        handleItemChange(index, 'imagen_url', response.url);
      } else {
        setError('Error al subir la imagen');
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      setError(error.response?.data?.error || 'Error al subir la imagen al servidor');
    } finally {
      setUploadingImage(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleRemoveImage = (index) => {
    handleItemChange(index, 'imagen_url', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!formData.cliente_nombre.trim()) {
      setError('El nombre del cliente es requerido');
      return;
    }

    const itemsValidos = items.filter(item => item.nombre.trim() && parseFloat(item.precio_unitario) > 0);
    if (itemsValidos.length === 0) {
      setError('Debe agregar al menos un producto con nombre y precio');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        items: itemsValidos,
        creado_por: user?.id
      };

      if (isEditing) {
        await cotizacionesApi.update(id, dataToSend);
      } else {
        const response = await cotizacionesApi.create(dataToSend);
        navigate(`/cotizaciones/${response.id}`);
        return;
      }

      navigate('/cotizaciones');
    } catch (error) {
      console.error('Error al guardar cotización:', error);
      setError(error.response?.data?.error || 'Error al guardar la cotización');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{isEditing ? 'Editar Cotización' : 'Nueva Cotización'}</h1>
          <p className="page-subtitle">
            {isEditing ? 'Modificar datos de la cotización' : 'Crear una nueva cotización para el cliente'}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/cotizaciones')}>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Datos del Cliente</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Nombre del Cliente *</label>
              <input
                type="text"
                name="cliente_nombre"
                className="form-control"
                value={formData.cliente_nombre}
                onChange={handleChange}
                placeholder="Nombre completo del cliente"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input
                type="tel"
                name="cliente_telefono"
                className="form-control"
                value={formData.cliente_telefono}
                onChange={handleChange}
                placeholder="Ej: 7725 5122"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input
                type="text"
                name="cliente_direccion"
                className="form-control"
                value={formData.cliente_direccion}
                onChange={handleChange}
                placeholder="Dirección del cliente"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Correo electrónico</label>
              <input
                type="email"
                name="cliente_correo"
                className="form-control"
                value={formData.cliente_correo}
                onChange={handleChange}
                placeholder="correo@ejemplo.com"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Configuración de Cotización</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha de Emisión</label>
              <input
                type="date"
                name="fecha_emision"
                className="form-control"
                value={formData.fecha_emision}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Validez (días)</label>
              <input
                type="number"
                name="validez_dias"
                className="form-control"
                value={formData.validez_dias}
                onChange={handleChange}
                min="1"
                max="90"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tienda</label>
              <select
                name="tienda_id"
                className="form-control"
                value={formData.tienda_id}
                onChange={handleChange}
              >
                {tiendas.map(tienda => (
                  <option key={tienda.id} value={tienda.id}>{tienda.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Productos / Servicios</h2>
            <button type="button" className="btn btn-primary btn-sm" onClick={agregarItem}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Agregar Producto
            </button>
          </div>

          {items.map((item, index) => (
            <div key={index} className="form-section" style={{ position: 'relative' }}>
              <div className="form-section-title">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Producto {index + 1}
                {items.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => eliminarItem(index)}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Imagen</label>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    background: uploadingImage[index] ? '#f0f0f0' : 'white'
                  }}>
                    {uploadingImage[index] ? (
                      <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
                    ) : item.imagen_url ? (
                      <>
                        <img
                          src={item.imagen_url}
                          alt="Producto"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            background: 'rgba(220, 53, 69, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '22px',
                            height: '22px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px'
                          }}
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <svg width="32" height="32" fill="none" stroke="#ccc" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                    {!item.imagen_url && !uploadingImage[index] && (
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => handleImageUpload(index, e)}
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                    )}
                  </div>
                  {!item.imagen_url && (
                    <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                      JPG, PNG, GIF, WebP (max 5MB)
                    </small>
                  )}
                </div>

                <div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Nombre del Producto *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.nombre}
                        onChange={(e) => handleItemChange(index, 'nombre', e.target.value)}
                        placeholder="Ej: Procesador Intel Core i5-12400"
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: '100px' }}>
                      <label className="form-label">Cantidad</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.cantidad}
                        onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: '150px' }}>
                      <label className="form-label">Precio Unitario *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.precio_unitario}
                        onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: '150px' }}>
                      <label className="form-label">Disponibilidad</label>
                      <select
                        className="form-control"
                        value={item.disponibilidad}
                        onChange={(e) => handleItemChange(index, 'disponibilidad', e.target.value)}
                      >
                        <option value="disponible">Disponible</option>
                        <option value="agotado">Agotado</option>
                        <option value="bajo_pedido">Bajo Pedido</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-control"
                      value={item.descripcion}
                      onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                      placeholder="Descripción detallada del producto o servicio..."
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Totales */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
                <span>Subtotal:</span>
                <strong>{formatearMoneda(calcularSubtotal())}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '300px' }}>
                <span>Descuento:</span>
                <input
                  type="number"
                  name="descuento_porcentaje"
                  className="form-control"
                  value={formData.descuento_porcentaje}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.5"
                  style={{ width: '80px', textAlign: 'right' }}
                />
                <span>%</span>
                <span style={{ marginLeft: 'auto' }}>-{formatearMoneda(calcularDescuento())}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '300px',
                borderTop: '2px solid #1a4f8b',
                paddingTop: '12px',
                fontSize: '18px'
              }}>
                <strong>TOTAL:</strong>
                <strong style={{ color: '#1a4f8b' }}>{formatearMoneda(calcularTotal())}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Mensaje de la Cotización</h2>
          </div>
          <div className="form-group">
            <textarea
              name="mensaje_cotizacion"
              className="form-control"
              value={formData.mensaje_cotizacion}
              onChange={handleChange}
              rows="3"
              placeholder="Mensaje que aparecerá al final de la cotización..."
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/cotizaciones')}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Guardando...
              </>
            ) : (
              <>
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {isEditing ? 'Guardar Cambios' : 'Crear Cotización'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CotizacionNueva;
